import React, { createContext, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

export const ThemeContext = createContext();

// Lista de cores disponíveis
export const COLOR_KEYS = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  ACCENT: "accent",
  SUCCESS: "success",
  WARNING: "warning",
  DANGER: "danger",
};

// Cores padrão
export const DEFAULT_COLORS = {
  primary: "#007bff",
  secondary: "#6c757d",
  accent: "#ff6b6b",
  success: "#28a745",
  warning: "#ffc107",
  danger: "#dc3545",
};

// Predefined theme presets
export const THEME_PRESETS = {
  default: {
    name: "Padrão",
    colors: DEFAULT_COLORS,
  },
  ocean: {
    name: "Oceano",
    colors: {
      primary: "#0066cc",
      secondary: "#4d94ff",
      accent: "#00d9ff",
      success: "#00cc88",
      warning: "#ffaa00",
      danger: "#ff3366",
    },
  },
  forest: {
    name: "Floresta",
    colors: {
      primary: "#1b6b3d",
      secondary: "#3d9970",
      accent: "#66cc99",
      success: "#22aa44",
      warning: "#dd8800",
      danger: "#dd3333",
    },
  },
  sunset: {
    name: "Pôr do Sol",
    colors: {
      primary: "#ff6b35",
      secondary: "#f7931e",
      accent: "#fdb833",
      success: "#77b28c",
      warning: "#ff9500",
      danger: "#d63230",
    },
  },
  midnight: {
    name: "Meia-Noite",
    colors: {
      primary: "#1a1a2e",
      secondary: "#16213e",
      accent: "#0f3460",
      success: "#00d4ff",
      warning: "#ffd60a",
      danger: "#ef476f",
    },
  },
  lavender: {
    name: "Lavanda",
    colors: {
      primary: "#8e7cc3",
      secondary: "#d8bfd8",
      accent: "#da70d6",
      success: "#98d8c8",
      warning: "#f7b801",
      danger: "#f5778c",
    },
  },
};

export const ThemeProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { darkMode, colors } = useSelector((state) => {
    const theme = state.darkTheme;
    
    // Garantir compatibilidade com estado anterior
    if (typeof theme === "boolean") {
      return { darkMode: theme, colors: DEFAULT_COLORS };
    }
    
    return {
      darkMode: theme.darkMode || false,
      colors: theme.colors || DEFAULT_COLORS,
    };
  });

  const applyThemeColors = useCallback((themeColors, isDark) => {
    const root = document.documentElement;
    
    // Aplicar cores customizadas
    Object.entries(themeColors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    // Aplicar modo escuro
    if (isDark) {
      root.setAttribute("data-theme", "dark");
      document.body.classList.add("dark");
    } else {
      root.removeAttribute("data-theme");
      document.body.classList.remove("dark");
    }
  }, []);

  // Aplicar cores como variáveis CSS
  useEffect(() => {
    applyThemeColors(colors, darkMode);
  }, [colors, darkMode, applyThemeColors]);

  const updateColor = useCallback((colorKey, colorValue) => {
    if (!COLOR_KEYS[colorKey.toUpperCase()] && !Object.values(COLOR_KEYS).includes(colorKey)) {
      console.warn(`Cor inválida: ${colorKey}`);
      return;
    }

    dispatch({
      type: "SET_SINGLE_COLOR",
      payload: { colorKey, value: colorValue },
    });
  }, [dispatch]);

  const updateColors = useCallback((newColors) => {
    dispatch({
      type: "SET_THEME_COLORS",
      payload: newColors,
    });
  }, [dispatch]);

  const applyPreset = useCallback((presetKey) => {
    const preset = THEME_PRESETS[presetKey];
    if (preset) {
      updateColors(preset.colors);
    }
  }, [updateColors]);

  const resetColors = useCallback(() => {
    dispatch({ type: "RESET_THEME_COLORS" });
  }, [dispatch]);

  const toggleDarkMode = useCallback(() => {
    dispatch({ type: "TOGGLE_DARK_MODE" });
  }, [dispatch]);

  const value = {
    darkMode,
    colors,
    updateColor,
    updateColors,
    applyPreset,
    resetColors,
    toggleDarkMode,
    colorKeys: COLOR_KEYS,
    presets: THEME_PRESETS,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme deve ser usado dentro de ThemeProvider");
  }
  return context;
};
